import { useState } from "react";
import { Button } from "@/components/ui/button";
import { usePosCart } from "@/hooks/pos/use-cart";
import { useSechPrice } from "@/hooks/pos/use-sech-price";
import { MenuTypesWithPoduct, ProductItemWithLocation, SelectedModifier } from "@/types/pos";
import { convertUsdToSech } from "@/lib/pos/sech-utils";
import DrinkBuilder from "@/components/pos/drink-builder";
import ModifierSelectionModal from "@/components/pos/modifier-selection-modal";
import { ChevronDown, ChevronUp, Flag, Flame, Leaf, Minus, Plus, Wine } from "lucide-react";
import { formatTime, isMenuAvailable } from "@/lib/pos/global";
import circleSparkle from '@/assets/pos/circle-sparkle.svg';
import semiCircleSparkle from '@/assets/pos/semicircle-sparkle.svg';
import semiStarSparkle from '@/assets/pos/semi-star-sparkle.svg';
import semiStarSparkleTwo from '@/assets/pos/semi-star-sparkle-2.svg';
import crossSparkle from '@/assets/pos/cross-sparkle.svg';
import fire from '@/assets/pos/mdi_fire.svg';
import raw from '@/assets/pos/noto_root-vegetable.svg';
import leaf from '@/assets/pos/ri_leaf-fill.svg';
import placeholder from '@/assets/pos/placeholder.png';
import noItemAvailable from '@/assets/pos/shopping-cart.svg';

interface DrinkMenuProps {
	menuTypes: MenuTypesWithPoduct[];
	isLoading?: boolean;
	selectedLocation: string;
	onOpenDrinkBuilder: () => void;
	onCloseDrinkBuilder: () => void;
	isCartOrderVisible?: boolean;
}

export default function DrinksMenu({ selectedLocation, menuTypes, isLoading, onOpenDrinkBuilder, onCloseDrinkBuilder, isCartOrderVisible }: DrinkMenuProps) {
	const {
		cartItems,
		addToCart,
		removeFromCart,
		updateQuantity,
	} = usePosCart()
	const { sechPrice } = useSechPrice();
	const [showDrinkBuilder, setShowDrinkBuilder] = useState(false);
	const [closedMenuIds, setClosedMenuIds] = useState<Set<number>>(new Set());
	const [collapsedSubcategories, setCollapsedSubcategories] = useState<Set<string>>(new Set());
	const [modifierModalProduct, setModifierModalProduct] = useState<ProductItemWithLocation | null>(null);

	const getProductCartItems = (productId: number) => {
		return cartItems.filter(
			item =>
				item.type === "product" &&
				item.productId === productId &&
				item.itemLocation === selectedLocation
		)
	}

	const getProductQuantity = (productId: number) => {
		return getProductCartItems(productId).reduce((sum, item) => sum + item.quantity, 0)
	}

	const incrementProduct = (product: ProductItemWithLocation) => {
		// If product has modifiers, always open the modal to select options
		if (product.modifierGroups && product.modifierGroups.length > 0) {
			setModifierModalProduct(product)
			return
		}

		// For products without modifiers, increment the single cart item
		const cartItems = getProductCartItems(product.id)
		if (cartItems.length > 0) {
			updateQuantity(cartItems[0].id, cartItems[0].quantity + 1)
		}
	}

	const decrementProduct = (product: ProductItemWithLocation) => {
		const productCartItems = getProductCartItems(product.id)
		if (productCartItems.length === 0) return

		// For products with modifiers, remove the last added item (last in array)
		// For products without modifiers, decrement the single item
		const lastItem = productCartItems[productCartItems.length - 1]

		if (lastItem.quantity <= 1) {
			removeFromCart(lastItem.id)
		} else {
			updateQuantity(lastItem.id, lastItem.quantity - 1)
		}
	}


	const toggleSubcategory = (menuId: number, subcategoryId: number) => {
		const key = `${menuId}-${subcategoryId}`;
		setCollapsedSubcategories(prev => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	};

	const isSubcategoryCollapsed = (menuId: number, subcategoryId: number) => {
		return collapsedSubcategories.has(`${menuId}-${subcategoryId}`);
	};


	// Group items by subcategory
	const groupBySubcategory = (items: ProductItemWithLocation[]) => {
		const groups: { subcategoryId: number; subcategoryName: string; items: ProductItemWithLocation[] }[] = [];
		const groupMap = new Map<number, ProductItemWithLocation[]>();
		const nameMap = new Map<number, string>();

		for (const item of items) {
			const subId = item.subcategory?.id ?? 0;
			const subName = item.subcategory?.name ?? "Other";

			if (!groupMap.has(subId)) {
				groupMap.set(subId, []);
				nameMap.set(subId, subName);
			}
			groupMap.get(subId)!.push(item);
		}

		groupMap.forEach((items, id) => {
			groups.push({
				subcategoryId: id,
				subcategoryName: nameMap.get(id) || "Other",
				items
			});
		});

		return groups;
	};

	const handleAddToCart = (product: ProductItemWithLocation) => {
		// If product has modifier groups, show the modal
		if (product.modifierGroups && product.modifierGroups.length > 0) {
			setModifierModalProduct(product);
			return;
		}

		// No modifiers, add directly
		const sechAmount = convertUsdToSech(product.priceUsd, sechPrice);
		addToCart({
			id: `product-${product.id}`,
			type: "product",
			productId: product.id,
			product,
			quantity: 1,
			unitPriceUsd: product.priceUsd,
			unitPriceSech: sechAmount,
			itemLocation: selectedLocation
		});
	};

	const handleModifierConfirm = (
		selectedModifiers: SelectedModifier[],
		totalPriceAdjustment: number,
		quantity: number
	) => {
		if (!modifierModalProduct) return;

		const basePrice = modifierModalProduct.priceUsd;
		const unitPriceWithModifiers = basePrice + totalPriceAdjustment;
		const sechAmount = convertUsdToSech(unitPriceWithModifiers, sechPrice);

		// Generate unique ID based on product + modifiers
		const modifierKey = selectedModifiers
			.map(m => m.modifierId)
			.sort()
			.join('-');
		const cartItemId = modifierKey
			? `product-${modifierModalProduct.id}-${modifierKey}`
			: `product-${modifierModalProduct.id}`;

		addToCart({
			id: cartItemId,
			type: "product",
			productId: modifierModalProduct.id,
			product: modifierModalProduct,
			quantity,
			unitPriceUsd: unitPriceWithModifiers,
			unitPriceSech: sechAmount,
			itemLocation: selectedLocation,
			selectedModifiers
		});

		setModifierModalProduct(null);
	};

	if (showDrinkBuilder) {
		return (
			<DrinkBuilder
				onClose={() => {
					setShowDrinkBuilder(false);
					onCloseDrinkBuilder();
				}}
				onAddToCart={(customDrink) => {
					addToCart({
						...customDrink,
						itemLocation: selectedLocation,
					});
					setShowDrinkBuilder(false);
					onCloseDrinkBuilder();
				}}
				selectedLocation={selectedLocation}
			/>
		);
	}

	if (isLoading) {
		return (
			<div className="p-4 space-y-4">
				<div className="animate-pulse bg-[var(--pos-accordian-disabled-btn-background)] h-24 rounded-xl" />
				{[1, 2, 3].map((i) => (
					<div
						key={i}
						className="bg-[var(--pos-primary-background-color)] rounded-xl shadow-sm border border-[var(--pos-layout-border-color)] overflow-hidden"
					>
						<div className="animate-pulse bg-[var(--pos-accordian-disabled-btn-background)] h-32 w-full" />
						<div className="p-4 space-y-2">
							<div className="animate-pulse bg-[var(--pos-accordian-disabled-btn-background)] h-4 w-3/4 rounded" />
							<div className="animate-pulse bg-[var(--pos-accordian-disabled-btn-background)] h-3 w-full rounded" />
						</div>
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="h-[calc(100vh-148px)]">
			{/* Drink Items */}
			<div className="h-full flex flex-col overflow-hidden">
				<div className={`flex-1 overflow-auto px-2 flex flex-col gap-2 ${isCartOrderVisible ? 'pb-[135px]' : 'pb-15'}`}>
					<div className="bg-[linear-gradient(135deg,#BE87FF,#9692FF)] relative px-2 py-3 rounded-lg">
						<h3 className="text-[16px] leading-[16px] font-bold font-domine  text-[var(--pos-fixed-light-color)]">Build Your Perfect Drink</h3>
						<img className="absolute z-0 h-6 w-6 bottom-0 right-[70px]" src={semiCircleSparkle} alt="" />
						<img className="absolute z-0 top-[16px] right-[70px]" src={circleSparkle} alt="" />
						<img className="absolute z-0 top-[-86px] right-[8px]" src={crossSparkle} alt="" />
						<img className="absolute z-0 right-[115px] top-0" src={semiStarSparkle} alt="" />
						<img className="absolute z-0 right-0 top-[24px]" src={semiStarSparkleTwo} alt="" />
						<p className="text-xs font-figtree leading-[16px] font-regular text-[var(--pos-fixed-light-color)] mt-1 mb-3">
							Choose your spirit, mixers, and extras
						</p>
						<Button
							onClick={() => { setShowDrinkBuilder(true); onOpenDrinkBuilder(); }}
							className="w-full relative z-10 gap-[4px] rounded-lg h-8 flex items-center justify-center bg-[var(--pos-fixed-light-color)] hover:bg-[var(--pos-fixed-light-color)]/90 font-figtree text-[#172B4D] text-[13px] leading-[15px] font-medium"
						>
							<Wine className="w-[18px] h-[18px] pointer-events-none" />
							Start Building
						</Button>
					</div>
					{!menuTypes?.length ? (
					<div className="flex items-center justify-center flex-col gap-2 h-full">
						<img src={noItemAvailable} alt="No Item Avaliable" />
						<span className="font-montserrat font-semibold text-[14px] leading-[18px] tracking-[0] text-center align-middle text-[var(--pos-primary-text-color)]">No Item Available</span>
						<p className="font-figtree font-medium text-[12px] leading-[16px] tracking-[0] text-center align-middle text-[var(--pos-secondary-text-color)] px-2">This item is currently unavailable. Try exploring other delicious options!</p>
					</div>
					) : (
						<>
							<div className="bg-[var(--pos-primary-background-color)] min-h-[106px] relative overflow-hidden py-3 px-3 rounded-[8px]">
								{/* Icon Legend */}
								<div className="bg-1 w-[168px] h-[168px] rounded-full absolute bg-[--pos-bg-color-1] blur-[120px] top-[-87px] left-[-87px]"></div>
								<div className="bg-2 w-[140px] h-[250px] rounded-full absolute bg-[--pos-bg-color-1] blur-[120px] top-[-125px] left-[calc(50%-70px)]"></div>
								<div className="bg-3 w-[184px] h-[184px] rounded-full absolute bg-[--pos-bg-color-3] blur-[120px] top-[calc(50%-92px)] right-[-90px]"></div>
								<div className="bg-4 w-[184px] h-[184px] rounded-full absolute bg-[--pos-bg-color-4] blur-[120px] top-0 right-0"></div>

								<div className="flex items-center mb-2">
									<div className="flex items-center gap-1 pr-4 border-r border-[var(--pos-layout-border-color)]">
										<img className="relative z-20" src={fire} alt="" />
										<span className="text-[11px] leading-[18px] font-semibold font-montserrat relative text-[var(--pos-primary-text-color)]">Spicy</span>
									</div>
									<div className="flex items-center gap-1 px-4 border-r border-[var(--pos-layout-border-color)]">
										<img className="relative z-20" src={leaf} alt="" />
										<span className="text-[11px] leading-[18px] font-semibold font-montserrat relative text-[var(--pos-primary-text-color)]">Vegan</span>
									</div>
									<div className="flex items-center gap-1 px-4 border-r border-[var(--pos-layout-border-color)]">
										<img className="relative z-20" src={raw} alt="" />
										<span className="text-[11px] leading-[18px] font-semibold font-montserrat relative text-[var(--pos-primary-text-color)]">Raw</span>
									</div>
									<div className="flex items-center gap-1 pl-4">
										<Flag className="w-4 h-4 text-[#E35151] fill-[#E35151] relative z-20" />
										<span className="text-[11px] leading-[18px] font-semibold font-montserrat relative text-[var(--pos-primary-text-color)]">Signature</span>
									</div>
								</div>
								{/* Raw/Undercooked Disclaimer */}
								<p className="text-xs leading-4 font-figtree relative text-[var(--pos-secondary-text-color)] text-start">
									Consuming RAW or undercooked Meats, Poultry, Seafood, Shellfish, Or Eggs May Increase Your Risk of Foodborne Illness, Especially if You Have Certain Medical Conditions.
								</p>
							</div>

							{menuTypes?.map(mt => {
								const available = isMenuAvailable(mt);
								const isOpen = !closedMenuIds.has(mt.id);
								const visible = mt.items;

								return (
									<div
										key={mt.id}
										className={`rounded-lg p-3 ${available
											? "bg-[var(--pos-primary-background-color)]"
											: "bg-[var(--pos-disabled-background-color)]"
											}`}
									>
										<div
											className="flex justify-between items-center cursor-pointer"
											onClick={() => available && setClosedMenuIds(prev => {
													const next = new Set(prev);
													if (next.has(mt.id)) {
														next.delete(mt.id);
													} else {
														next.add(mt.id);
													}
													return next;
												})}
										>
											<div className="w-[calc(100%-37px)]">
												<h2 className={`font-bold text-base font-montserrat leading-[18px] mb-1 ${available ? "text-[var(--pos-primary-text-color)]" : "text-[var(--pos-disabled-text-color)]"}`}>{mt.name}</h2>
												<div className="flex items-center gap-2">
													<p className={`text-xs font-figtree leading-[16px] font-medium ${available ? "text-[var(--pos-secondary-text-color)]" : "text-[var(--pos-disabled-text-color)]"}`}>
														{`Time: ${formatTime(mt.startTime)} - ${formatTime(mt.endTime)}`}
													</p>
												</div>
													{!available && (
														<>
															<span
															className={`font-medium font-figtree text-xs leading-[16px] ${
  																		  available
  																		    ? "text-[var(--pos-secondary-text-color)]"
  																		    : "text-[var(--pos-disabled-text-color)]"
  																		}`}>{mt?.name} is not available right now.</span>

															<p className={`font-medium font-figtree text-xs leading-[16px] ${
  																		  available
  																		    ? "text-[var(--pos-secondary-text-color)]"
  																		    : "text-[var(--pos-disabled-text-color)]"
  																		}`}>Next service starts at {formatTime(mt?.startTime)}</p>
														</>
													)}
											</div>
											<div className={`h-[28px] w-[28px] border-0 flex justify-center items-center rounded-full ${available ? "bg-[--pos-accordian-enabled-btn-background] text-[--pos-accordian-enabled-btn-color]" : "disabled-count-btn"}`}>{isOpen ?
												<Minus className="icon h-[18px] w-[18px]" /> :
												<Plus className="icon h-[18px] w-[18px]" />}
											</div>
										</div>

										{isOpen && available && (
											<div className="mt-4 pt-4 border-t border-dashed border-[var(--pos-layout-border-color)]">
												{visible?.length > 0 && groupBySubcategory(visible).map((group) => {
													const isCollapsed = isSubcategoryCollapsed(mt.id, group.subcategoryId);
													return (
														<div key={group.subcategoryId} className="mb-4 last:mb-0 bg-[var(--pos-secondary-background-color)] rounded-lg p-3">
															{/* Subcategory Header */}
															<button
																onClick={() => toggleSubcategory(mt.id, group.subcategoryId)}
																className="w-full flex items-center justify-between py-2"
															>
																<h3 className="font-domine text-[16px] leading-[20px] font-bold text-[var(--pos-primary-text-color)]">
																	{group.subcategoryName}
																</h3>
																<div className="flex items-center gap-2">
																	<span className="text-xs font-figtree text-[var(--pos-secondary-text-color)]">
																		{group.items.length} {group.items.length === 1 ? 'item' : 'items'}
																	</span>
																	{isCollapsed ? (
																		<ChevronDown className="w-5 h-5 text-[var(--pos-secondary-text-color)]" />
																	) : (
																		<ChevronUp className="w-5 h-5 text-[var(--pos-secondary-text-color)]" />
																	)}
																</div>
															</button>

															{/* Subcategory Items */}
															{!isCollapsed && <div className="mt-3 pt-3 border-t border-dashed border-[var(--pos-layout-border-color)]">
																{group.items.map(item => (
																<div
																	key={item.id}
																	className="border-b flex items-start justify-between border-dashed border-[var(--pos-layout-border-color)] pb-5 mb-4 last:border-b-0 last:pb-0 last:mb-0"
																>
																	<div className="flex flex-col items-start flex-1 min-w-0 mr-2">
																		<div className="flex items-center gap-1 mb-1">
																			{item.isSpicy && (
																				<Flame className="w-4 h-4 text-[#E35151] fill-[#E35151]" />
																			)}
																			{item.isVegan && (
																				<Leaf className="w-4 h-4 text-[#22C55E] fill-[#22C55E]" />
																			)}
																			{item.isRaw && (
																				<img src={raw} alt="Raw" className="w-4 h-4" />
																			)}
																			{item.isSignature && (
																				<Flag className="w-4 h-4 text-[#E35151] fill-[#E35151]" />
																			)}
																			<p className="font-semibold font-montserrat text-sm leading-[18px] text-[var(--pos-primary-text-color)]">{item.name}</p>
																		</div>
																		<div className="flex flex-row-reverse items-center gap-2">
																			<p className="font-medium font-montserrat text-xs leading-[18px] text-[var(--pos-primary-text-color)]">${Number(item.priceUsd).toFixed(2)}</p>
																		</div>
																		<p className="text-xs font-figtree leading-[16px] w-full break-words font-medium text-[var(--pos-secondary-text-color)] mt-2">
																			{item.description}
																		</p>
																	</div>
																	<div className="flex flex-col items-center justify-center w-[109px]">
																		<img
																			src={item.imageUrl || placeholder}
																			alt={item.name}
																			className="w-[103px] h-[104px] rounded-lg object-cover"
																			onError={(e) => {
																				e.currentTarget.src = placeholder;
																			}}
																		/>
																		{(() => {
																			const quantity = getProductQuantity(item.id)
																			const hasModifiers = item.modifierGroups && item.modifierGroups.length > 0

																			if (quantity === 0) {
																				return (
																					<Button
																						onClick={() => handleAddToCart(item)}
																						className="bg-[--pos-negative-selected-color] mt-[-12px] min-w-[72px] font-montserrat h-[28px] flex items-center justify-center px-2 text-[10px] leading-[18px] font-semibold rounded-sm text-[--pos-primary-color] border border-[--pos-tertiary-btn-border-color]"
																					>
																						Add
																					</Button>
																				)
																			}

																			// For products with modifiers, show just a count badge - users manage in cart
																			if (hasModifiers) {
																				return (
																					<button
																						onClick={() => handleAddToCart(item)}
																						className="mt-[-12px] flex items-center justify-center h-[28px] min-w-[28px] px-2 border border-[--pos-tertiary-btn-border-color] rounded-sm bg-[--pos-negative-selected-color]"
																					>
																						<span className="text-[12px] font-semibold text-[#DC2626]">
																							{quantity}
																						</span>
																						<Plus className="w-3 h-3 ml-1 text-[#DC2626]" />
																					</button>
																				)
																			}

																			return (
																				<div className="mt-[-12px] flex items-center gap-2 h-[28px] px-2 border border-[--pos-tertiary-btn-border-color] rounded-sm bg-[--pos-negative-selected-color]">
																					<button
																						onClick={() => decrementProduct(item)}
																						className="w-5 h-5 flex items-center justify-center text-[#DC2626]"
																					>
																						<Minus className="w-3 h-3" />
																					</button>

																					<span className="text-[12px] min-w-[12px] text-center font-semibold text-[#DC2626]">
																						{quantity}
																					</span>

																					<button
																						onClick={() => incrementProduct(item)}
																						className="w-5 h-5 flex items-center justify-center text-[#DC2626]"
																					>
																						<Plus className="w-3 h-3" />
																					</button>
																				</div>
																			)
																		})()}
																	</div>
																</div>
															))}
															</div>}
														</div>
													);
												})}
												{!visible?.length && (
													<div className="flex items-center justify-center flex-col gap-2 h-full">
														<img src={noItemAvailable} alt="No Item Avaliable" />
														<span className="font-montserrat font-semibold text-[14px] leading-[18px] tracking-[0] text-center align-middle text-[var(--pos-primary-text-color)]">No Item Available</span>
														<p className="font-figtree font-medium text-[12px] leading-[16px] tracking-[0] text-center align-middle text-[var(--pos-secondary-text-color)] px-2">This item is currently unavailable. Try exploring other delicious options!</p>
													</div>
												)}
											</div>
										)}
									</div>
								);
							})}
						</>
					)}
				</div>


			</div>

			{/* Modifier Selection Modal */}
			{modifierModalProduct && (
				<ModifierSelectionModal
					product={modifierModalProduct}
					isOpen={!!modifierModalProduct}
					onClose={() => setModifierModalProduct(null)}
					onConfirm={handleModifierConfirm}
				/>
			)}
		</div>
	);
}
